import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@/components/Accordion';
import { AudioDetailDto } from '@/service/interface';
import useFlags from '@jenmusic/feature-flag';
import dayjs from 'dayjs';
import React from 'react';
import { useSessionStore } from '@/hooks/useSessionStore';

type PropertiesProps = {
  selectedSongDetail: AudioDetailDto;
};

export const Properties: React.FC<PropertiesProps> = ({ selectedSongDetail }) => {
  const { showAdvancedDetails } = useFlags();
  const sessionStore = useSessionStore();
  const { session } = sessionStore;
  const hasAdvancedSFStrengthPermission = session?.user?.permissions?.some(({ code }) => code === 'VIEW_ADVANCED_SF_STRENGTH');

  return (
    <div className="items-center">
      <Accordion type="single" collapsible>
        <AccordionItem value="item-1">
          <AccordionTrigger className="px-[16px] pr-[8px] h-[48px] border-t border-jen-border">
            <div className=" text-jen-size-heading-07 text-jen-content-tertiary">PROPERTIES</div>
          </AccordionTrigger>
          <AccordionContent className="mb-4 px-[16px] pr-[8px] mt-[20px] font-pressura-mono text-[12px] leading-[16px] text-jen-content-secondary">
            {(selectedSongDetail?.metadata?.filterWeight || selectedSongDetail?.metadata?.filterStrengthSetting) && (
              <PropertiesItem
                label="STYLE FILTER STRENGTH"
                value={getFilterStrengthSetting(selectedSongDetail, hasAdvancedSFStrengthPermission)}
              />
            )}
            {showAdvancedDetails && <PropertiesItem label="MODEL" value={getModelDisplayedVersion(selectedSongDetail)} />}
            <PropertiesItem label="CREATED ON" value={dayjs(selectedSongDetail?.created_at).format('h:mm a DD MMM YY')} />
            <PropertiesItem label="FORMAT" value={selectedSongDetail?.metadata?.format} />
            <PropertiesItem label="PLAYS" value={selectedSongDetail?.plays} />
            <PropertiesItem label="VISIBILITY" value={selectedSongDetail?.is_published ? 'PUBLISHED' : 'PRIVATE'} />
            {!!selectedSongDetail?.metadata.bpm && <PropertiesItem label="BPM" value={selectedSongDetail?.metadata.bpm} />}
            {!!selectedSongDetail?.metadata.key && <PropertiesItem label="KEY" value={selectedSongDetail?.metadata.key} />}
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
};

export const PropertiesItem = ({ label, value }: { label: string; value?: string | number }) => {
  return (
    <div className=" flex justify-between mt-jen-spacing-4">
      <div>{label}</div>
      <div>{value}</div>
    </div>
  );
};

function getModelDisplayedVersion(audio: AudioDetailDto) {
  if (
    audio.model_version === 'jenxl_genre_sf_v20241206_c20241218_c' ||
    audio.model_version === 'jen_16-base-v20241206-c20241218-genre_sf-c' ||
    audio.model_version === 'jen_16-base-v20241206-c20241218-17cd-c'
  ) {
    return '1.6+';
  }
  if (audio.model_version?.slice(0, 24) === 'jenxl_RAG_1min_v20241206') {
    return '1.6';
  }
  if (audio.model_version?.slice(0, 11) === 'jen_17-base') {
    return '1.7';
  }
  if (audio.model_version?.slice(0, 6) === 'jen_20') {
    return '2.0';
  }
  return '';
}

function getFilterStrengthSetting(audio: AudioDetailDto, hasAdvancedSFStrengthPermission = false) {
  if (audio.metadata.filterStrengthSetting !== undefined) {
    if (hasAdvancedSFStrengthPermission) {
      if (audio.metadata.filterStrengthSetting === 0) return '01';
      if (audio.metadata.filterStrengthSetting === 0.25) return '02';
      if (audio.metadata.filterStrengthSetting === 0.5) return '03';
      if (audio.metadata.filterStrengthSetting === 0.75) return '04';
      if (audio.metadata.filterStrengthSetting === 1) return '05';
    } else {
      if (audio.metadata.filterStrengthSetting === 0) return 'LOW';
      if (audio.metadata.filterStrengthSetting === 0.5) return 'MED';
      if (audio.metadata.filterStrengthSetting === 1) return 'HIGH';
    }
  } else {
    // we can remove code when we release SF, this is only for handling old data
    const threshold1 = dayjs('2025-02-04');
    const date = dayjs(audio.created_at);
    if (date.isAfter(threshold1)) {
      if (audio.metadata.filterWeight === 0.2) return 'LOW';
      if (audio.metadata.filterWeight === 0.3) return 'MED';
      if (audio.metadata.filterWeight === 0.4) return 'HIGH';
      else {
        if (audio.metadata.filterWeight === 0.4) return 'LOW';
        if (audio.metadata.filterWeight === 0.5) return 'MED';
        if (audio.metadata.filterWeight === 0.7) return 'HIGH';
      }
    }
    return '';
  }
}
